/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useId, useState } from "react";

import "@js-joda/core";
import "@js-joda/timezone";
import {
  ProductTemplateEntryType,
  useCreateProductTemplateEntry,
  useDeleteProductTemplateEntry,
  useGetProductTemplateConfig,
  useGetProductTemplateData,
  useGetProductTemplateFilterPreviewData,
  useSignProductTemplateEntry,
  useUpdateProductTemplateEntry,
} from "api/api-hooks/product-templates-hooks";
import ExportDataTableButton from "components/basic/export-data-table/ExportDataTable";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  DashboardComponentPropsBase,
  IComponentTimed,
} from "components/pages/dashboard/components/interfaces";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import useProductTemplateImportEntries from "hooks/dashboard/useProductTemplateImportEntries";
import useTimeState from "hooks/dashboard/useTimeState";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner";
import { I, T } from "services/lang/LanguageService";
import { CellType, SignStatus, TemplateLoadingType } from "utils/enums";
import { addToDate, formatDateTimeOrNow } from "utils/time-utils";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import "./ProductTemplateTable.scss";
import ProductTemplateTableDefault from "./ProductTemplateTableDefault";
import ProductTemplateTableTransposed from "./ProductTemplateTableTransposed";
import ProductTemplateFilter from "./components/ProductTemplateFilter";
import EditDataCell from "./components/templates/table-components/edit-datacell";
import {
  IDateRange,
  IPeriodLoadingConfig,
  ITableDataEntry,
  ITableDataEntryValue,
  LoadingConfig,
  ProductTemplate,
  ReportOptions,
  SignProductTemplateEntryResponse,
} from "./utils/interfaces";
import { getTemplateLoadingRange } from "./utils/timeUtils";
import { IUserSignature } from "./utils/cellInterfaces";

export interface IProductTemplateTableProps
  extends DashboardComponentPropsBase<ReportOptions>,
    IDateRange,
    IComponentTimed,
    ProductTemplate.IHorizontalCommonConfig {
  safeName: string;
  transposed?: boolean;
  showTitle?: boolean;
  showTotals?: boolean;
  updateFrequency?: number;
  hiddenColumns?: Array<string>;
  onlyShownColumns?: Array<string>;
  itemsPerPage?: number;
  overrideLoadingConfig?: LoadingConfig;
  filter?: string | null;
  defaultFilter?: string;
  onFetchError?: (val: string) => void;
  exactDateFrom?: Date;
  exactDateTo?: Date;
  hideDates?: boolean;
  showFilter?: boolean;
  useExternalFilterEndpoint?: boolean;
  allowAddEntry?: boolean;
  allowRowEdit?: boolean;
  foreignKeyWrap?: boolean;
  hideHeader?: boolean;
  hidePagination?: boolean;
  onFilterChange?: (filter: string) => void;
}

export default function ProductTemplateTable(props: IProductTemplateTableProps) {
  const id = useId();
  const setStatus = useComponentStateCallback(props);
  const [filter, setFilter] = useState<string | null | undefined>(props.filter);

  const allowRowEdit = props.allowRowEdit ?? true;
  const transposed = !!props.transposed;
  const showTitle = props.showTitle ?? true;

  const [datesChanged, setDatesChanged] = useState<boolean>(false);

  const [allowAddEntry, setAllowAddEntry] = useState<boolean>(allowRowEdit);
  const [allowDelete, setAllowDelete] = useState<boolean>(true);

  const [disabledDatesFrom, setDisabledDatesFrom] = useState<Date[] | undefined>([]);
  const [disabledDatesTo, setDisabledDatesTo] = useState<Date[] | undefined>([]);

  const [selectedEntryIndex, setSelectedEntryIndex] = useState<number | undefined>(undefined);

  const [selectedEntryId, setSelectedEntryId] = useState<string | undefined>("");
  const [selectedEntryBody, setSelectedEntryBody] = useState<ITableDataEntry | undefined>(
    undefined
  );

  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);

  const [page, setPage] = useState(0);

  const [updateFrequency, setUpdateFrequency] = useState<number | boolean>(
    props.updateFrequency && (props.reportMode === undefined || !props.reportMode)
      ? props.updateFrequency * 1000
      : false
  );
  const [fetchData, setFetchData] = useState(true);
  const [displayedTemplateData, setDisplayedTemplateData] = useState<ITableDataEntry[]>([]);

  useEffect(() => {
    setFilter(props.filter);
  }, [props.filter]);

  const getProductTemplateDataSelectedFunction = props.useExternalFilterEndpoint
    ? useGetProductTemplateFilterPreviewData
    : useGetProductTemplateData;

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  useEffect(() => {
    if (props.from) timeState.setFrom(props.from);
    if (props.to) timeState.setTo(props.to);
  }, [props.from, props.to]);
  
  const params: ProductTemplateEntryType = {
    safeName: props.safeName,
    dateFrom: timeState.from?.toISOString(),
    dateTo: timeState.to?.toISOString(),
    filter: filter ?? props.defaultFilter,
  };

  const { mutate: createNewEntry, status: createNewEntryStatus } = useCreateProductTemplateEntry(
    params,
    {
      onSuccess: () => {
        notifySuccess(T("alert.success"));
      },
      onFailure: () => {
        notifyError(T("alert.failed-to-create-entry"));
      },
    }
  );
  const { mutate: updateNewEntry } = useUpdateProductTemplateEntry(params, {
    onSuccess: () => {
      notifySuccess(T("alert.updated"));
    },
    onFailure: () => {
      notifyError(T("alert.failed-to-update-entry"));
    },
  });
  const { mutate: deleteEntry } = useDeleteProductTemplateEntry(params, {
    onSuccess: () => {
      notifySuccess(T("alert.deleted"));
    },
    onFailure: () => {
      notifyError(T("alert.failed-to-delete-entry"));
    },
  });

  const { mutate: signEntryData, data: signData } = useSignProductTemplateEntry(params, {
    onSuccess: data => {
      if (data.data.signStatus === SignStatus.CONDITIONS_NOT_MET) {
        setErrorDialogOpen(true);
      } else {
        notifySuccess(T("alert.updated"));
      }
    },
    onFailure: () => {
      notifyError(T("alert.failed-to-update-entry"));
    },
  });

  const {
    data: configData,
    isError: configError,
    isLoading: isLoadingConfig,
  } = useGetProductTemplateConfig(props.safeName, id, props.hiddenColumns, 
    props.onlyShownColumns, {
    usePromiseHandler: (res: any) => {
      if (res.error) {
        console.log(res.error.message);
        throw res.error;
      }

      const { data } = res;

      if (props.hiddenColumns && props.hiddenColumns.length > 0) {
        const attributes = data.data.attributes.filter((attribute: any) => {
          return !props.hiddenColumns?.includes(attribute.referenceName);
        });

        return {
          ...data,
          data: {
            ...data.data,
            attributes,
          },
        };
      }
      if (props.onlyShownColumns && props.onlyShownColumns.length > 0) {
        const attributes = data.data.attributes.filter((attribute: any) => {
          return props.onlyShownColumns?.includes(attribute.referenceName);
        });

        return {
          ...data,
          data: {
            ...data.data,
            attributes,
          },
        };
      }

      return data;
    },
  });

  const {
    data: templateData,
    isError: templateError,
    isLoading: isLoadingTemplate,
    isFetching: isTemplateDataFetching,
  } = getProductTemplateDataSelectedFunction(params, {
    enabled: !!configData && !!timeState.from && !!timeState.to && fetchData,
    refetchInterval: updateFrequency,
    retry: false,
    staleTime: 0,
    onError: er => {
      if (props.onFetchError) {
        props.onFetchError(er.message);
      }
    },
  });

  const { handleFileChange } = useProductTemplateImportEntries(configData, params);
  
  const handleButtonClick = () => {
    setFetchData(true);
    setDatesChanged(true);
    setUpdateFrequency(false);
  };

  useEffect(() => {
    if (templateData) {
      setDisplayedTemplateData(templateData);
    }
  }, [fetchData, templateData]);

  useEffect(() => {
    if (!isLoadingConfig && configData) {
      setAllowAddEntry(props.allowAddEntry ?? configData.data?.config?.allowRowAddition ?? true);
      setAllowDelete(configData.data?.config?.allowRowDeletion ?? true);
      setDatesByConfigAndLoadingType();
    }
  }, [configData, props.from, props.to, props.referenceTime]);

  useEffect(() => {
    if (timeState.from) {
      disableDateDays(timeState.from, -1, setDisabledDatesTo);
    }
    if (timeState.to) {
      disableDateDays(timeState.to, 1, setDisabledDatesFrom);
    }
  }, [timeState.from, timeState.to]);

  useEffect(() => {
    if (props.exactDateFrom && !props.from) {
      timeState.setFrom(props.exactDateFrom);
    }
    if (props.exactDateTo && !props.to) {
      timeState.setTo(props.exactDateTo);
    }
  }, [props.exactDateFrom, props.exactDateTo]);

  const setDatesByConfigAndLoadingType = useCallback(
    (wasClicked = false) => {
      if (configData && !props.exactDateFrom && !props.exactDateTo) {
        const loadingType: TemplateLoadingType = props.overrideLoadingConfig?.loadingType
          ? props.overrideLoadingConfig?.loadingType
          : configData?.data?.config?.loadingType!;
        const loadingConfig: IPeriodLoadingConfig = {
          ...configData?.data?.config?.loadingConfig,
          ...props.overrideLoadingConfig?.loadingConfig,
        };

        const { start, end } = getTemplateLoadingRange(
          {
            loadingType,
            loadingConfig,
          },
          props.referenceTime
        );
        if (!props.from && !props.to) {
          timeState.setFrom(start);
          timeState.setTo(end);
        }

        if (wasClicked && fetchData) {
          setFetchData(prevState => !prevState);
        }
        setDatesChanged(false);
      }
    },
    [
      configData,
      fetchData,
      props.exactDateFrom,
      props.exactDateTo,
      props.from,
      props.overrideLoadingConfig?.loadingConfig,
      props.overrideLoadingConfig?.loadingType,
      props.referenceTime,
      props.to,
    ]
  );

  const disableDateDays = (initDate: Date, step: number, setter: CallableFunction) => {
    const date = new Date(initDate);
    const year = date.getFullYear();
    const allDays = [];
    while (
      date.getFullYear() === year - 1 ||
      date.getFullYear() === year ||
      date.getFullYear() === year + 1
    ) {
      const d = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
        .getDate()
        .toString()
        .padStart(2, "0")}`;
      allDays.push(new Date(d));
      date.setDate(date.getDate() + step);
    }
    setter(allDays);
  };

  const editedDataHandler = (
    indexEntry: number,
    refName: string,
    newValue: any,
    newCell?: ITableDataEntryValue
  ) => {
    if (selectedEntryId && selectedEntryBody) {
      const tempEntry = { ...selectedEntryBody };

      if (tempEntry.entries[refName] != null) {
        tempEntry.entries[refName].value = newValue;
      } else if (newCell) {
        tempEntry.entries[newCell.referenceName] = newCell;
      }
      setSelectedEntryBody(tempEntry);
    }
  };

  const manageRowEdit = useCallback(
    (entryIndex: number, editable: boolean) => {
      if (editable && displayedTemplateData && displayedTemplateData.length >= entryIndex) {
        const offsetedIndex = entryIndex + page;

        setSelectedEntryBody(JSON.parse(JSON.stringify(displayedTemplateData[offsetedIndex])));
        setSelectedEntryId(displayedTemplateData[offsetedIndex].id);
        setSelectedEntryIndex(entryIndex);
        setUpdateFrequency(false);
      } else {
        if (!datesChanged) {
          setUpdateFrequency(
            props.updateFrequency && (props.reportMode === undefined || !props.reportMode)
              ? props.updateFrequency * 1000
              : false
          );
        }
        setSelectedEntryId(undefined);
        setSelectedEntryBody(undefined);
        setSelectedEntryIndex(undefined);
      }
    },
    [datesChanged, displayedTemplateData, page, props.reportMode, props.updateFrequency]
  );

  const onClickEdit = useCallback(
    (entryIndex: number) => {
      manageRowEdit(entryIndex, true);
    },
    [manageRowEdit]
  );

  const onClickCopy = useCallback(
    (currentIndex: number) => {
      if (displayedTemplateData && displayedTemplateData.length > currentIndex) {
        const offsetedIndex = currentIndex + page;

        const entry = { ...displayedTemplateData[offsetedIndex] };
        const filteredEntriesList = Object.values(entry.entries).filter(
          (ob: any) => ob.type !== CellType.USER_SIGNATURE
        );

        createNewEntry({ body: filteredEntriesList });
      }
    },
    [createNewEntry, displayedTemplateData, page]
  );

  const onClickSave = useCallback(
    (entryIndex: number) => {
      if (selectedEntryId && selectedEntryBody) {
        updateNewEntry({
          entryId: selectedEntryId,
          body: Object.values(selectedEntryBody.entries),
        });
      }
      manageRowEdit(entryIndex, false);
    },
    [manageRowEdit, selectedEntryBody, selectedEntryId, updateNewEntry]
  );

  const onClickDelete = useCallback(
    (entryIndex: number) => {
      const offsetedIndex = entryIndex + page;

      if (displayedTemplateData && displayedTemplateData[offsetedIndex].id != null) {
        deleteEntry({ entryId: displayedTemplateData[offsetedIndex].id!! });

        if (selectedEntryId != null || selectedEntryId !== "") {
          manageRowEdit(entryIndex, false);
        }
      } else {
        notifyError(T("alert.entry-index-not-found"));
      }
    },
    [deleteEntry, displayedTemplateData, manageRowEdit, page, selectedEntryId]
  );

  const onClickCancel = useCallback(
    (entryIndex: number) => {
      manageRowEdit(entryIndex, false);
    },
    [manageRowEdit]
  );

  const onClickSign = (entryIndex: number, referenceName: string) => {
    if (displayedTemplateData) {
      const offsetedIndex = entryIndex + page;

      const entryID = displayedTemplateData[offsetedIndex].id;
      if (entryID != null) {
        signEntryData({ entryId: entryID, referenceName });
      }
    }
  };

  const onClickAddEntry = () => {
    let body: Array<any> = [];
    const dateString = formatDateTimeOrNow("yyyy-MM-dd HH:mm");

    if (configData) {
      const val = configData?.data?.attributes.find(
        (ob: any) => ob.referenceName === configData.keyColumn
      );

      if (val) {
        if (val.type === "DATETIME") {
          body = [
            {
              referenceName: val.referenceName,
              type: val.type,
              value: dateString,
            },
          ];
        }
        createNewEntry({ body });
        return;
      }
      easasAlert({
        summary: T("alert.failed-to-create-entry"),
        detail: T("alert.column-with-key-not-found", { keyColumn: configData.keyColumn }),
        severity: "error",
      });
    }
  };

  const notifySuccess = (msg: string) => {
    easasAlert({ summary: T("alert.success"), detail: msg, severity: "success" });
  };

  const notifyError = (msg: string) => {
    easasAlert({ summary: T("alert.error"), detail: msg, severity: "error" });
  };

  const EditRowDataCell = useCallback(
    (val: ITableDataEntry, index: number) => {
      let disableEdit = "";

      if (
        configData &&
        configData.data?.config?.signatureAttribute &&
        val.entries[configData.data.config.signatureAttribute] &&
        (val.entries[configData.data.config.signatureAttribute].value as IUserSignature).signed
      ) {
        disableEdit = (val.entries[configData.data.config.signatureAttribute].value as IUserSignature).display;
      }

      return (
        <EditDataCell
          key={val.id}
          index={index}
          onClickCopy={onClickCopy}
          onClickEdit={onClickEdit}
          onClickDelete={onClickDelete}
          onClickSave={onClickSave}
          onClickCancel={onClickCancel}
          isInEditMode={selectedEntryId === val.id}
          deletable={allowDelete}
          signedBy={disableEdit}
        />
      );
    },
    [
      allowDelete,
      configData,
      onClickCancel,
      onClickCopy,
      onClickDelete,
      onClickEdit,
      onClickSave,
      selectedEntryId,
    ]
  );

  const handleKeyEnterPress = () => {
    if (selectedEntryIndex != null) {
      onClickSave(selectedEntryIndex);
    }
  };

  const handleTableKeyDown = (key: string) => {
    if (key === "Enter" && selectedEntryId) {
      handleKeyEnterPress();
    }
  };

  const handleFixedPeriodChange = (e: any) => {
    timeState.setFrom(e);
    if (
      configData?.data?.config &&
      configData.data.config.loadingType === TemplateLoadingType.FIXED_PERIOD
    ) {
      const tempLoadingConf: IPeriodLoadingConfig = {
        ...configData.data.config.loadingConfig,
        ...props.overrideLoadingConfig?.loadingConfig,
      };
      const newDateTo = addToDate(e, tempLoadingConf.period as number, tempLoadingConf.periodUnit);
      timeState.setTo(newDateTo);
    }
  };

  const handleFilterApply = useCallback(
    (filterLine: string) => {
      setFilter(props.filter ? `${props.filter}${filterLine ? `;${filterLine}` : ""}` : filterLine);
      if (props?.onFilterChange) props.onFilterChange(filterLine);
      setFetchData(true);
    },
    [props.filter]
  );

  const DateToConfigInitializeButton = useCallback(() => {
    if (datesChanged) {
      return (
        <div>
          <Button
            icon="pi pi-history"
            style={{ transform: "scale(95%)" }}
            onClick={() => {
              setDatesChanged(false);
              setDatesByConfigAndLoadingType(true);
              setFetchData(true);
              setUpdateFrequency(
                props.updateFrequency && (props.reportMode === undefined || !props.reportMode)
                  ? props.updateFrequency * 1000
                  : false
              );
            }}
            title={T("word.return-default-dates")}
            disabled={selectedEntryId !== "" && selectedEntryIndex != null}
          />
        </div>
      );
    }
    return null;
  }, [
    datesChanged,
    props.reportMode,
    props.updateFrequency,
    selectedEntryId,
    selectedEntryIndex,
    setDatesByConfigAndLoadingType,
  ]);

  const printSplitColumns = () => {
    if (props.reportOptions && props.reportOptions.splitAfter) {
      const data = [...(displayedTemplateData || [])];
      const resultArray = [];

      for (let i = 0; i < data.length; i += props.reportOptions.splitAfter) {
        const chunk = data.slice(i, i + props.reportOptions.splitAfter);
        resultArray.push(chunk);
      }

      return resultArray.map((smallChunkArray, i) => {
        return (
          <ProductTemplateTableTransposed
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            configData={configData}
            templateData={smallChunkArray}
            editedDataHandler={editedDataHandler}
            selectedEntryBody={selectedEntryBody}
            selectedEntryIndex={selectedEntryIndex}
            selectedEntryId={selectedEntryId}
            onClickSign={onClickSign}
            handleTableKeyDown={handleTableKeyDown}
            itemsPerPage={props.itemsPerPage}
            page={page}
            setPage={setPage}
            EditRowDataCell={EditRowDataCell}
            reportOptions={props.reportOptions}
            foreignKeyWrap={props.foreignKeyWrap}
          />
        );
      });
    }

    return <>{T("message.number-of-columns-to-break-is-not-defined")}</>;
  };

  const printSplitRows = () => {
    if (props.reportOptions && props.reportOptions.splitAfter) {
      const data = [...(displayedTemplateData || [])];
      const resultArray = [];

      for (let i = 0; i < data.length; i += props.reportOptions.splitAfter) {
        const chunk = data.slice(i, i + props.reportOptions.splitAfter);
        resultArray.push(chunk);
      }

      return resultArray.map((smallChunkArray, i) => {
        return (
          <ProductTemplateTableDefault
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            configData={configData}
            templateData={smallChunkArray}
            editedDataHandler={editedDataHandler}
            selectedEntryBody={selectedEntryBody}
            selectedEntryIndex={selectedEntryIndex}
            selectedEntryId={selectedEntryId}
            onClickSign={onClickSign}
            page={page}
            setPage={setPage}
            handleTableKeyDown={handleTableKeyDown}
            EditRowDataCell={EditRowDataCell}
            itemsPerPage={props.itemsPerPage}
            reportOptions={props.reportOptions}
            editColumnLocation={props.editColumnLocation}
            allowRowEdit={allowRowEdit}
            foreignKeyWrap={props.foreignKeyWrap}
            showTotals={props.showTotals}
          />
        );
      });
    }

    return <>{T("message.number-of-columns-to-break-is-not-defined")}</>;
  };

  const calendarMinuteStep = 5;

  useEffect(() => {
    if (configError || templateError) {
      setStatus({ state: "error", error: T("error.no-data-found") });
    } else if (!configData) {
      if (!isLoadingConfig && !isLoadingTemplate) {
        setStatus({ state: "error", error: T("error.something-went-wrong") });
      }
    } else if (configData && templateData) {
      setStatus({ state: "finished" });
    }
  }, [configError, templateError, configData, templateData, isLoadingConfig, isLoadingTemplate]);

  useEffect(() => {
    if (
      configData?.data?.config &&
      configData.data.config.loadingType === TemplateLoadingType.FIXED_PERIOD &&
      timeState.from &&
      datesChanged
    ) {
      const tempLoadingConf: IPeriodLoadingConfig = {
        ...configData.data.config.loadingConfig,
        ...props.overrideLoadingConfig?.loadingConfig,
      };
      const newDateTo = addToDate(
        timeState.from,
        tempLoadingConf.period as number,
        tempLoadingConf.periodUnit
      );
      timeState.setTo(newDateTo);
    }
  }, [timeState.from]);

  const ErrorOnTemplateBody = useCallback(() => {
    if (configError || templateError) {
      return (
        <div className="demo-container p-p-4">
          <Error text={T("error.no-data-found")} />
        </div>
      );
    }
    return (
      <div className="demo-container p-p-4">
        <ProgressSpinner className="p-d-block p-mx-auto" />
      </div>
    );
  }, [configError, templateError]);

  if (!configData) {
    if (!isLoadingConfig && !isLoadingTemplate) {
      return (
        <div>
          <Error text={T("error.something-went-wrong")} />
        </div>
      );
    }
    return (
      <div>
        <div className="demo-container p-p-4">
          <ProgressSpinner className="p-d-block p-mx-auto" />
        </div>
      </div>
    );
  }

  const truncateStartUnit =
    props.overrideLoadingConfig?.loadingConfig?.truncateStartUnit ||
    configData?.data?.config?.loadingConfig?.truncateStartUnit;

  const showTimeOnCalendar = truncateStartUnit === "HOURS" || truncateStartUnit === "MINUTES";

  const signResponse: SignProductTemplateEntryResponse | undefined = signData?.data;
  const uniqueColumns = new Set(
    signResponse?.validationErrors?.map(err => err.attributeDisplayName)
  );

  const chooseOptions = {
    label: `${T("action.import")} CSV`,
    icon: "pi pi-file-import",
    className: "p-button-success text-sm h-full",
  };

  return (
    <div className="product-template-full-window">
      <div className="product-template-wrapper">
        {showTitle && (
          <div className="title-window non-printable">{configData && I(configData.displayName)}</div>
        )}

        <div className="product-template-header non-printable">
          {!props.hideDates && (
            <>
              {configData &&
                configData?.data?.config &&
                configData.data.config.loadingType === TemplateLoadingType.FIXED_PERIOD && (
                  <div className="calendars-and-button-wrapper">
                    <div className="calendars-wrapper">
                      {T("word.date")}:
                      <CalendarWrapper
                        id="time24"
                        value={timeState.from}
                        style={{ paddingLeft: "10px" }}
                        onChange={e => handleFixedPeriodChange(e)}
                        showTime={showTimeOnCalendar}
                        disabled={selectedEntryId !== "" && selectedEntryIndex != null}
                        dateFormat="yy-mm-dd"
                        stepMinute={calendarMinuteStep}
                      />
                    </div>
                    <Button
                      onClick={handleButtonClick}
                      icon="pi pi-play"
                      className="p-button-success"
                      title={T("action.fetch-data")}
                    />
                    <DateToConfigInitializeButton />
                  </div>
                )}

              {configData &&
                configData?.data?.config?.loadingType === TemplateLoadingType.RANGE && !props.hideHeader && (
                  <div className="calendars-and-button-wrapper flex flex-wrap">
                    <div className="calendars-wrapper flex flex-wrap">
                      <div className="calendars-wrapper">
                        {T("word.date-from")}
                        <CalendarWrapper
                          id="time24"
                          value={timeState.from}
                          style={{ paddingLeft: "10px" }}
                          onChange={e => {
                            timeState.setFrom(e);
                            setFetchData(false);
                          }}
                          showTime
                          dateFormat="yy-mm-dd"
                          disabled={selectedEntryId !== "" && selectedEntryIndex != null}
                          disabledDates={disabledDatesFrom}
                          stepMinute={calendarMinuteStep}
                        />
                      </div>
                      <div className="calendars-wrapper">
                      {T("word.date-to")}
                        <CalendarWrapper
                          id="time24"
                          value={timeState.to}
                          onChange={e => {
                            timeState.setTo(e);
                            setFetchData(false);
                          }}
                          showTime
                          dateFormat="yy-mm-dd"
                          disabled={selectedEntryId !== "" && selectedEntryIndex != null}
                          disabledDates={disabledDatesTo}
                          stepMinute={calendarMinuteStep}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleButtonClick}
                      icon="pi pi-play"
                      className="p-button-success"
                      title={T("action.fetch-data")}
                      loading={isTemplateDataFetching}
                    />
                    <DateToConfigInitializeButton />
                  </div>
                )}
            </>
          )}

          {/* Justification space between. PlaceHolder */}
          <div />

          {configData && !props.hideHeader &&(
            <div className="buttons-flex-wrapper">
              {props.showFilter && (
                <ProductTemplateFilter
                  defaultFilter={props.defaultFilter}
                  secondaryFilter={props.filter}
                  onApplyFilter={handleFilterApply}
                  attributes={configData.data?.attributes ?? []}
                />
              )}
              {allowAddEntry && (
                <>
                <Button
                  label={T("action.add-entry")}
                  className="p-button-sm p-button-success"
                  icon="pi pi-plus"
                  onClick={() => onClickAddEntry()}
                  disabled={
                    createNewEntryStatus === "pending" ||
                    (selectedEntryId !== "" && selectedEntryIndex != null)
                  }
                  loading={createNewEntryStatus === "pending"}
                  />
                <FileUpload
                  mode="basic"
                  maxFileSize={1000000}
                  customUpload
                  chooseOptions={chooseOptions}
                  auto
                  accept=".csv"
                  uploadHandler={handleFileChange}
                />
              </>

              )}
              <ExportDataTableButton
                header={configData}
                body={displayedTemplateData}
                tableName={props.safeName}
                transposed={transposed}
                showTotal={props.showTotals}
              />
            </div>
          )}
        </div>

        {displayedTemplateData ? (
          displayedTemplateData.length > 0 ? (
            transposed ? (
              props.reportOptions?.splitAfter &&
              displayedTemplateData.length > props.reportOptions?.splitAfter ? (
                printSplitColumns()
              ) : (
                <ProductTemplateTableTransposed
                  configData={configData}
                  templateData={displayedTemplateData}
                  editedDataHandler={editedDataHandler}
                  selectedEntryBody={selectedEntryBody}
                  selectedEntryIndex={selectedEntryIndex}
                  selectedEntryId={selectedEntryId}
                  onClickSign={onClickSign}
                  handleTableKeyDown={handleTableKeyDown}
                  EditRowDataCell={EditRowDataCell}
                  itemsPerPage={props.itemsPerPage}
                  page={page}
                  setPage={setPage}
                  reportOptions={props.reportOptions}
                  foreignKeyWrap={props.foreignKeyWrap}
                  showTotals={props.showTotals}
                  hidePagination={props.hidePagination}
                />
              )
            ) : props.reportOptions?.splitAfter &&
              displayedTemplateData.length > props.reportOptions?.splitAfter ? (
              printSplitRows()
            ) : (
              <ProductTemplateTableDefault
                configData={configData}
                templateData={displayedTemplateData}
                editedDataHandler={editedDataHandler}
                selectedEntryBody={selectedEntryBody}
                selectedEntryIndex={selectedEntryIndex}
                selectedEntryId={selectedEntryId}
                onClickSign={onClickSign}
                handleTableKeyDown={handleTableKeyDown}
                EditRowDataCell={EditRowDataCell}
                page={page}
                setPage={setPage}
                itemsPerPage={props.itemsPerPage}
                reportOptions={props.reportOptions}
                editColumnLocation={props.editColumnLocation}
                allowRowEdit={allowRowEdit}
                foreignKeyWrap={props.foreignKeyWrap}
                showTotals={props.showTotals}
                hidePagination={props.hidePagination}
              />
            )
          ) : isTemplateDataFetching || (templateData && templateData?.length > 0) ? (
            <ProgressSpinner className="overflow-hidden" />
          ) : (
            <div className="demo-container p-p-4">
              <Error text={T("word.no-data")} />
            </div>
          )
        ) : (
          <ErrorOnTemplateBody />
        )}
      </div>
      <Dialog
        header={T("word.conditions-are-not-met")}
        headerClassName="text-red-500 gap-3"
        visible={errorDialogOpen}
        onHide={() => setErrorDialogOpen(false)}
        blockScroll
        className="text-xl"
      >
        {[...uniqueColumns]?.map(col => (
          <div key={col} className="mx-5">
            <h6>{`${col}:`}</h6>
            <ul>
              {signResponse?.validationErrors?.map(
                err =>
                  err.attributeDisplayName === col && (
                    <li key={err?.message}>
                      {err?.message || T("error.unknown-validation-message")}
                    </li>
                  )
              )}
            </ul>
          </div>
        ))}
      </Dialog>
    </div>
  );
}

interface IError {
  text: string;
}

export function Error(props: IError) {
  return (
    <div style={{ margin: "auto", width: "50%", padding: "10px" }}>
      <div
        className="demo-container p-p-4"
        style={{ border: "2px solid #3C76DA", borderRadius: "25px" }}
      >
        <h3 className="p-d-block p-mx-auto" style={{ textAlign: "center" }}>
          {props.text}
        </h3>
      </div>
    </div>
  );
}
